import React, { Fragment, useEffect, useRef, useState } from "react";
import Loader from "@/components/Loaders/LoaderCircle/Loader";

import styles from "./MinecraftSkin.module.scss";
import useWindowDimensions from "@/hooks/useWindowDimensions";

interface MinecraftSkinProps {
  url: string;
  toggleAnimation: boolean;
  staticPose?: "walking" | "standing";
}

const MinecraftSkin = (props: MinecraftSkinProps) => {
  const skinContainerRef = useRef<HTMLDivElement | null>(null);
  const { isMobile } = useWindowDimensions();
  const startTime = Date.now();

  const _skinRender = useRef(null);
  const skinRenderEventListener = useRef<((e: any) => void) | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [mobileAnimation, setMobileAnimation] = useState(true);
  const [rendererLoaded, setRendererLoaded] = useState(false);
  const [mouseIsDown, setMouseIsDown] = useState(false);
  const [skinLoading, setSkinLoading] = useState(true);
  const skinLoadingRef = useRef(true);
  const shouldAnimate =
    props.toggleAnimation && ((isMobile && mobileAnimation) || !isMobile);

  useEffect(() => {
    const loadScript = (
      src: string,
      integrity?: string,
      crossorigin?: string
    ) => {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        if (integrity) script.integrity = integrity;
        if (crossorigin) script.crossOrigin = crossorigin;
        script.onload = () => resolve(0);
        script.onerror = () =>
          reject(new Error(`Failed to load script ${src}`));
        document.body.appendChild(script);
      });
    };

    const loadDependencies = async () => {
      await loadScript(
        "https://cdnjs.cloudflare.com/ajax/libs/jquery/3.3.1/jquery.min.js",
        "sha256-FgpCb/KJQlLNfOu91ta32o/NMZxltwRo8QtmkMRdAu8=",
        "anonymous"
      );
      await loadScript(
        "https://cdnjs.cloudflare.com/ajax/libs/three.js/94/three.min.js",
        "sha256-NGC9JEuTWN4GhTj091wctgjzftr+8WNDmw0H8J5YPYE=",
        "anonymous"
      );
      await loadScript("./skin.min.js");
    };
    loadDependencies()
      .then(() => {
        setLoaded(true);
      })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    if (!loaded || !skinContainerRef.current || !props.url) return;

    skinLoadingRef.current = true;
    setSkinLoading(true);

    if (_skinRender.current) {
      const skinRender = _skinRender.current as any;
      if (skinRender._scene?.children) {
        skinRender.clearScene(true);
      }
      // Defer to next frame so Three.js camera is re-initialized after clearScene
      const raf = requestAnimationFrame(() => {
        skinRender.render(props.url);
      });
      return () => cancelAnimationFrame(raf);
    }

    if (!(window as any).SkinRender) return;

    const canvasWidth = skinContainerRef.current.offsetWidth;
    const canvasHeight = skinContainerRef.current.offsetHeight;

    const createRenderer = (width: number, height: number) => {
      if (!skinContainerRef.current) return;
      _skinRender.current = new (window as any).SkinRender(
        {
          controls: { keys: false },
          canvas: { width, height },
          pauseHidden: false,
          autoResize: true,
          sendStats: false,
        },
        skinContainerRef.current
      );
      // Defer render() to next frame — canvas must be laid out before
      // SkinRender initialises its Three.js OrthographicCamera (.right etc.)
      const raf = requestAnimationFrame(() => {
        if (_skinRender.current) {
          (_skinRender.current as any).render(props.url);
          setRendererLoaded(true);
        }
      });
      return raf;
    };

    if (canvasWidth === 0 || canvasHeight === 0) {
      const retryTimeout = setTimeout(() => {
        if (!skinContainerRef.current) return;
        const w = skinContainerRef.current.offsetWidth;
        const h = skinContainerRef.current.offsetHeight;
        if (w > 0 && h > 0) createRenderer(w, h);
      }, 100);
      return () => clearTimeout(retryTimeout);
    }

    const raf = createRenderer(canvasWidth, canvasHeight);
    return () => { if (raf) cancelAnimationFrame(raf); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.url, loaded]);

  useEffect(() => {
    if (rendererLoaded) {
      skinRenderEventListener.current = function (e: any) {
        if (skinLoadingRef.current) {
          skinLoadingRef.current = false;
          setSkinLoading(false);
        }
        const t = (Date.now() - startTime) / 1000;
        if (e?.detail?.playerModel?.children?.length === 6) {
          if (shouldAnimate) {
            e.detail.playerModel.children[2].rotation.x = Math.sin(t * 5) / 2;
            e.detail.playerModel.children[3].rotation.x = -Math.sin(t * 5) / 2;
            e.detail.playerModel.children[4].rotation.x = Math.sin(t * 5) / 2;
            e.detail.playerModel.children[5].rotation.x = -Math.sin(t * 5) / 2;
          } else if (props.staticPose === "walking") {
            e.detail.playerModel.rotation.y = -Math.PI / 6;
            // Arms - natural swing
            e.detail.playerModel.children[2].rotation.x = -0.6; // Right arm back
            e.detail.playerModel.children[2].rotation.z = -0.1; // Right arm slightly out
            e.detail.playerModel.children[3].rotation.x = 0.6; // Left arm forward
            e.detail.playerModel.children[3].rotation.z = 0.1; // Left arm slightly out
            // Legs - wider step
            e.detail.playerModel.children[4].rotation.x = 0.7; // Right leg forward (wider)
            e.detail.playerModel.children[5].rotation.x = -0.7; // Left leg back (wider)
          } else {
            // Standing pose: all limbs straight
            e.detail.playerModel.children[2].rotation.x = 0;
            e.detail.playerModel.children[3].rotation.x = 0;
            e.detail.playerModel.children[4].rotation.x = 0;
            e.detail.playerModel.children[5].rotation.x = 0;
          }
        }
      };

      (_skinRender.current as any).element.addEventListener(
        "skinRender",
        skinRenderEventListener.current
      );

      return () => {
        if (skinRenderEventListener.current) {
          (_skinRender.current as any).element.removeEventListener(
            "skinRender",
            skinRenderEventListener.current
          );
        }
      };
    }
  }, [rendererLoaded, shouldAnimate]);

  const handleMouseDown = () => {
    setMouseIsDown(true);
    setTimeout(() => {
      setMouseIsDown(false);
    }, 500);
  };

  const handleMouseUp = () => {
    if (mouseIsDown && isMobile) {
      setMobileAnimation(!mobileAnimation);
    }
    setMouseIsDown(false);
  };

  // Fallback: hide spinner if skinRender event never fires (e.g. bad URL)
  useEffect(() => {
    if (!skinLoading) return;
    const timeout = setTimeout(() => {
      skinLoadingRef.current = false;
      setSkinLoading(false);
    }, 10000);
    return () => clearTimeout(timeout);
  }, [skinLoading, props.url]);

  return (
    <Fragment>
      <div
        className={styles.minecraftSkinContainer}
        id="minecraftSkinContainer"
        ref={skinContainerRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        {skinLoading && (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-xl"
            style={{ zIndex: 10, background: "var(--foreground)" }}
          >
            <Loader isLoading={true} width={36} height={36} color="white" />
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default MinecraftSkin;
